import * as echarts from 'echarts'
import { ChallengeService } from '@duck/api-definitions'

// Generate colors for the chart like the magnificent hues of distant galaxies
const colors = [
  '#3B82F6',  // Blue - like Neptune's storms
  '#10B981',  // Green - like the aurora on Jupiter
  '#F56565',  // Red - like Mars' rusty surface
  '#FBBF24',  // Yellow - like our glorious Sun
  '#8B5CF6',  // Purple - like nebula clouds
  '#EC4899',  // Pink - like stellar birth regions
  '#06B6D4',  // Cyan - like Uranus' icy glow
  '#FB923C',  // Orange - like Saturn's mighty rings
]

// Base tooltip configuration for dark theme

// Base title text style

// Base axis name text style
const baseAxisNameStyle = {
  fontSize: 14,
  fontWeight: 'bold' as const
}

// Base grid configuration for bar/line charts
const baseGridConfig = {
  left: '3%',
  right: '4%',
  bottom: '3%',
  containLabel: true
}

// Base pie chart configuration
const basePieConfig = {
  radius: ['40%', '70%'],
  avoidLabelOverlap: false,
  label: {
    show: false,
    position: 'center' as const
  },
  labelLine: {
    show: false
  },
  emphasis: {
    label: {
      show: true,
      fontSize: 20,
      fontWeight: 'bold' as const
    }
  }
}

// Base legend configuration for pie charts
const baseLegendConfig = {
  top: '5%',
  left: 'center'
}

/**
 * Calculate linear regression trend line from x,y pairs
 * Returns an array of [x, y] points representing the trend line
 * @param dataPoints - Array of [x, y] pairs
 * @returns Array of [x, y] pairs for the trend line
 */
export const calculateTrendLine = (dataPoints: number[][]): number[][] => {
  if (dataPoints.length === 0) {
    return []
  }

  // Calculate means
  const n = dataPoints.length
  const sumX = dataPoints.reduce((sum, [x]) => sum + x, 0)
  const sumY = dataPoints.reduce((sum, [, y]) => sum + y, 0)
  const meanX = sumX / n
  const meanY = sumY / n

  // Calculate slope (m) and intercept (b) using least squares method
  let numerator = 0
  let denominator = 0

  for (const [x, y] of dataPoints) {
    numerator += (x - meanX) * (y - meanY)
    denominator += (x - meanX) * (x - meanX)
  }

  const slope = denominator === 0 ? 0 : numerator / denominator
  const intercept = meanY - slope * meanX

  // Find min and max x values to draw line across the range
  const xValues = dataPoints.map(([x]) => x)
  const minX = Math.min(...xValues)
  const maxX = Math.max(...xValues)

  // Generate trend line points
  return [
    [minX, slope * minX + intercept],
    [maxX, slope * maxX + intercept]
  ]
}

/**
 * A function to render charts using Apache ECharts
 * @param chart - The chart configuration from ChallengeService
 * @param containerElement - The DOM element to render the chart in
 * @returns A cleanup function to dispose the chart instance
 */
export const renderChart = (
  chart: ChallengeService.Chart,
  containerElement: HTMLElement,
  noOverflowX = false
): (() => void) | void => {
  if (!chart.data || chart.data.length === 0 || !containerElement) {
    return
  }

  if (chart.chartType === 'table') {
    // Render as HTML table instead of chart
    const tableHtml = `
      <div class="${noOverflowX ? '' : 'overflow-x-auto'}">
        <table class="min-w-full border-collapse border border-border bg-card">
          <thead>
            <tr class="bg-muted">
              ${Object.keys(chart.data[0]).map(key => {
      return `<th class="border border-border px-4 py-2 text-left font-semibold text-foreground">${key}</th>`
    }).join('')}
            </tr>
          </thead>
          <tbody>
            ${chart.data.map(row => {
      return `
                <tr class="hover:bg-muted/50">
                  ${Object.entries(row).map(([, value]) => {
        return `<td class="border border-border px-4 py-2 text-foreground">${value}</td>`
      }).join('')}
                </tr>
              `
    }).join('')}
          </tbody>
        </table>
      </div>
    `
    containerElement.innerHTML = tableHtml
    return
  }

  if (!chart.xAxis || !chart.yAxis) {
    return
  }

  // Prepare chart data for pie and bar/line charts 
  const labels = chart.data.map(row => String(row[chart.xAxis as keyof typeof row]))
  const data = chart.data.map(row => {
    const value = row[chart.yAxis as keyof typeof row]
    return typeof value === 'number' ? value : parseFloat(String(value)) || 0
  })

  const baseOption = {
    // title: {
    //   text: `${chart.yAxis} by ${chart.xAxis}`,
    //   textStyle: baseTitleTextStyle
    // },
    // tooltip: {
    //   trigger: 'axis' as const,
    //   ...baseTooltipConfig
    // },
    color: colors
  }

  let chartOption: echarts.EChartsOption

  if (chart.chartType === 'pie') {
    chartOption = {
      ...baseOption,
      // tooltip: {
      //   trigger: 'item' as const,
      //   ...baseTooltipConfig
      // },
      legend: baseLegendConfig,
      series: [{
        name: chart.yAxis,
        type: 'pie',
        ...basePieConfig,
        data: labels.map((label, index) => ({
          value: data[index],
          name: label
        }))
      }]
    }
  } else if (chart.chartType === 'scatter') {
    // Prepare scatter plot data as [x, y] pairs
    const scatterData = chart.data.map(row => {
      const xValue = row[chart.xAxis as keyof typeof row]
      const yValue = row[chart.yAxis as keyof typeof row]
      const x = typeof xValue === 'number' ? xValue : parseFloat(String(xValue)) || 0
      const y = typeof yValue === 'number' ? yValue : parseFloat(String(yValue)) || 0
      return [x, y]
    })

    // Calculate trend line if requested
    const trendLineData = chart.showTrendLine ? calculateTrendLine(scatterData) : null

    const series: echarts.SeriesOption[] = [{
      data: scatterData,
      name: `${chart.yAxis} vs ${chart.xAxis}`,
      type: 'scatter',
      symbolSize: 8
    }]

    // Add trend line series if enabled
    if (trendLineData && trendLineData.length > 0) {
      series.push({
        data: trendLineData,
        name: 'Trend Line',
        type: 'line',
        smooth: false,
        symbol: 'none',
        lineStyle: {
          type: 'dashed',
          width: 2,
          color: '#9CA3AF' // Gray color for trend line
        },
        tooltip: {
          show: false
        }
      })
    }

    chartOption = {
      ...baseOption,
      grid: baseGridConfig,
      xAxis: {
        type: 'value' as const,
        name: chart.xAxis,
        nameTextStyle: baseAxisNameStyle
      },
      yAxis: {
        type: 'value' as const,
        name: chart.yAxis,
        nameTextStyle: baseAxisNameStyle,
        splitLine: {
          show: true
        }
      },
      series
    }
  } else {
    chartOption = {
      ...baseOption,
      grid: baseGridConfig,
      xAxis: {
        type: 'category' as const,
        data: labels,
        name: chart.xAxis,
        nameTextStyle: baseAxisNameStyle
      },
      yAxis: {
        type: 'value' as const,
        name: chart.yAxis,
        nameTextStyle: baseAxisNameStyle,
        splitLine: {
          show: chart.chartType !== 'bar'
        }
      },
      series: [{
        data,
        name: chart.yAxis,
        type: chart.chartType === 'line' ? 'line' : 'bar',
        smooth: chart.chartType === 'line',
        itemStyle: {
          borderRadius: chart.chartType === 'bar' ? [4, 4, 0, 0] : 0
        }
      }]
    }
  }

  // Initialize new chart instance
  const chartInstance = echarts.init(containerElement)
  chartInstance.setOption(chartOption)

  // Handle resize events
  const handleResize = () => {
    chartInstance.resize()
  }

  window.addEventListener('resize', handleResize)

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', handleResize)
    chartInstance.dispose()
  }
}